// The right pane: one video, in focus.
//
// The preview player lives above the tabs rather than inside one, because it
// is the subject and the tabs are things you do to it -- grabbing a poster
// frame in Artwork and marking a chapter in Chapters both need the same
// playhead, and neither should own the player.

import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyTwoToneIcon from '@mui/icons-material/ContentCopyTwoTone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PreviewPlayer from '../components/Previewplayer';
import SubtitleManager from '../components/SubtitleManager';
import ChapterManager from '../components/ChapterManager';
import { useAdmin } from './AdminStore';
import { describeError, ENDPOINTS } from './api';
import { SUBTITLE_LANGUAGES, deriveStatus } from './schema';
import { writeBool } from '../ps/format';
import StatusLeds from './StatusLeds';
import useVideoForm from './useVideoForm';
import OverviewTab from './tabs/OverviewTab';
import MediaTab from './tabs/MediaTab';
import ArtworkTab from './tabs/ArtworkTab';

const TABS = ['Overview', 'Media', 'Subtitles', 'Chapters', 'Artwork'];

export function VideoDetail({ identifier, onDuplicate, onBack }) {
  const { videos, applyRecord, remove, toast, patch, setDirty } = useAdmin();
  // hasOwnProperty, not plain indexing: `videos.constructor` is inherited from
  // Object.prototype, and a pane rendering the Object constructor as a video is
  // a stranger failure than an empty pane.
  const video = Object.prototype.hasOwnProperty.call(videos, identifier)
    ? videos[identifier]
    : undefined;

  const [tab, setTab] = useState(0);
  const [showPlayer, setShowPlayer] = useState(false);
  const [playhead, setPlayhead] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [markingFinished, setMarkingFinished] = useState(false);
  const playerRef = useRef(null);
  const paneRef = useRef(null);

  // The Overview buffer lives here rather than in the tab, so moving between
  // tabs cannot discard what has been typed.
  const buffer = useVideoForm({ identifier, video, patch, toast });
  const { isDirty, save } = buffer;

  // A different video means a different everything. The tab is deliberately
  // kept, so working through the library one Subtitles tab at a time does not
  // mean re-selecting it for each video.
  useEffect(() => {
    setShowPlayer(false);
    setPlayhead(null);
  }, [identifier]);

  // Published so the shell can stop a selection change from silently throwing
  // the buffer away.
  useEffect(() => {
    setDirty(isDirty);
    return () => setDirty(false);
  }, [isDirty, setDirty]);

  // ⌘S / Ctrl-S otherwise opens the browser's save dialog, which is never what
  // you want in a form.
  //
  // The listener is on the window, so it also fires from inside a dialog that
  // is covering this pane -- saving a form you cannot see. MUI marks everything
  // behind an open modal aria-hidden, which is exactly the question being asked
  // here: is this pane currently the thing you are looking at?
  useEffect(() => {
    const onKey = (event) => {
      const modalOnTop = paneRef.current?.closest('[aria-hidden="true"]');
      if (modalOnTop) return;
      if ((event.metaKey || event.ctrlKey) && event.key?.toLowerCase() === 's') {
        event.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save]);

  if (!video) {
    return (
      <Box className="admin-detail" sx={{ display: 'grid', placeItems: 'center' }}>
        <Typography sx={{ color: 'var(--ps-text-dim)', fontSize: 12.5 }}>
          Select a video, or press / to search.
        </Typography>
      </Box>
    );
  }

  const status = deriveStatus(video);
  const watchUrl = `${ENDPOINTS.LEARN}/watch/${identifier}`;

  // The counts turn the tab strip into a summary, and the dot means the buffer
  // is dirty even while you are looking at another tab. Subtitles count out of
  // five rather than up from one, so a video that is three quarters subtitled
  // says so here instead of looking like a video with three subtitles.
  const tabLabel = (name) => {
    if (name === 'Overview') return isDirty ? 'Overview •' : name;
    if (name === 'Subtitles' && status.subtitlesPartial) {
      return `${name} · ${status.subtitlesPresent}/${SUBTITLE_LANGUAGES.length}`;
    }
    if (name === 'Subtitles' && video.subtitles?.length) return `${name} · ${video.subtitles.length}`;
    if (name === 'Chapters' && video.chapters?.length) return `${name} · ${video.chapters.length}`;
    return name;
  };

  const copy = (text, what) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast(`${what} copied.`, 'success'))
      .catch(() => toast('Could not copy to the clipboard.', 'error'));
  };

  const doDelete = async () => {
    try {
      await remove(identifier);
      setConfirmDelete(false);
      toast(`Deleted ${identifier}.`, 'success');
    } catch (error) {
      toast(describeError(error, 'The video was not deleted.'), 'error');
    }
  };

  const notify = (message, severity) => toast(message, severity);
  const onUpdate = (record) => applyRecord(identifier, record);

  // Stored as the same 'true'/'false' string as the other flags, so nothing on
  // the site can misread it the way the boolean flags were misread.
  const toggleFinished = async () => {
    setMarkingFinished(true);
    try {
      await patch(identifier, { finished: writeBool(!status.finished) });
      toast(status.finished ? 'Reopened.' : 'Marked finished.', 'success');
    } catch (error) {
      toast(describeError(error, 'The marker was not changed.'), 'error');
    } finally {
      setMarkingFinished(false);
    }
  };

  return (
    <section className="admin-detail" ref={paneRef}>
      <Box
        sx={{
          p: '10px 22px',
          borderBottom: '1px solid var(--ps-line)',
          background: 'linear-gradient(var(--ps-surface-top), var(--ps-bg-panel))',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {onBack && (
            <Button size="small" onClick={onBack} sx={{ display: { md: 'none' } }}>
              Back
            </Button>
          )}
          <Typography sx={{ fontSize: 16, fontWeight: 500, minWidth: 0 }}>
            {video.title || identifier}
          </Typography>
          <StatusLeds video={video} />

          {/* The marker is the workflow state; the lamps are advice about what
              is missing. A video can be finished with gaps, and complete but
              not yet marked. */}
          <Tooltip
            describeChild
            title={
              status.finished
                ? 'Marked finished — click to reopen'
                : status.ready
                  ? 'Everything on the checklist is done — mark it finished'
                  : 'Mark finished anyway (the checklist still has gaps)'
            }
          >
            <Button
              size="small"
              variant={status.finished ? 'contained' : 'outlined'}
              startIcon={
                status.finished ? (
                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 14 }} />
                )
              }
              onClick={toggleFinished}
              disabled={markingFinished}
              sx={status.ready && !status.finished ? { borderColor: 'var(--ps-gold)' } : undefined}
            >
              {status.finished ? 'Finished' : 'Mark finished'}
            </Button>
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          <Tooltip title="Copy the unlocked share link">
            <Button size="small" onClick={() => copy(`${watchUrl}?unlckd=true`, 'Share link')}>
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            </Button>
          </Tooltip>
          <Tooltip title="Duplicate this video's settings">
            <Button size="small" onClick={() => onDuplicate(identifier)}>
              <ContentCopyTwoToneIcon sx={{ fontSize: 15 }} />
            </Button>
          </Tooltip>
          <Tooltip title="Open on the site">
            <Button size="small" component="a" href={watchUrl} target="_blank" rel="noopener">
              <OpenInNewIcon sx={{ fontSize: 15 }} />
            </Button>
          </Tooltip>
          {/* Quiet: it shows a face on hover and still asks before deleting. */}
          <Tooltip title="Delete this video">
            <Button
              size="small"
              variant="text"
              onClick={() => setConfirmDelete(true)}
              sx={{ '&:hover': { color: 'var(--ps-mute)' } }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
            </Button>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '2px' }}>
          <span className="admin-label admin-mono" style={{ color: 'var(--ps-text-dim)' }}>
            {identifier}
          </span>
          {video.category && <span className="admin-label">{video.category}</span>}
          {status.video && (
            <Button size="small" variant="text" onClick={() => setShowPlayer((v) => !v)}>
              {showPlayer ? 'Hide preview' : 'Preview'}
            </Button>
          )}
        </Box>
      </Box>

      {showPlayer && status.video && (
        <Box sx={{ p: '10px 22px', borderBottom: '1px solid var(--ps-line-subtle)' }}>
          <Box sx={{ maxWidth: 560, mx: 'auto' }}>
            <PreviewPlayer ref={playerRef} videoData={video} onPlayhead={setPlayhead} />
          </Box>
        </Box>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: '22px' }} variant="scrollable">
        {TABS.map((name) => (
          <Tab
            key={name}
            label={tabLabel(name)}
            sx={name === 'Overview' && isDirty ? { color: 'var(--ps-gold)' } : undefined}
          />
        ))}
      </Tabs>

      <div className="admin-detail__scroll">
        {tab === 0 && <OverviewTab identifier={identifier} video={video} buffer={buffer} />}
        {tab === 1 && <MediaTab identifier={identifier} video={video} />}
        {tab === 2 && (
          <SubtitleManager
            videoData={video}
            videoIdentifier={identifier}
            onUpdateVideoData={onUpdate}
            onNotify={notify}
          />
        )}
        {tab === 3 && (
          <ChapterManager
            videoData={video}
            videoIdentifier={identifier}
            onUpdateVideoData={onUpdate}
            onNotify={notify}
            playhead={showPlayer ? playhead : null}
          />
        )}
        {tab === 4 && (
          <ArtworkTab
            identifier={identifier}
            video={video}
            captureFrame={() => playerRef.current?.captureFrame()}
          />
        )}
      </div>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete video</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            Delete "{video.title || identifier}" from the catalog? Its subtitles, chapters and
            artwork go with it. The api.video file itself is not deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

export default VideoDetail;
